import { BrowserAudienceRuntime } from './audience/runtime.js';
import { SdkApiClient } from './api/client.js';
import { BrowserStore } from './storage/browser-store.js';
import { buildStudyIframeUrl } from './study/context.js';
import { assertNonEmpty, mergePresentationOptions, noopLogger, normalizeError, normalizeBaseUrl, resolvePresentationMode, shortHash, } from './shared.js';
import { WidgetShell } from './ui/widget-shell.js';
export class PillowSDK {
    constructor(options) {
        this.sessionStoreKey = 'study-sessions';
        this.listeners = {
            studyPresent: new Set(),
            studySkip: new Set(),
            studyFinish: new Set(),
            studyError: new Set(),
        };
        this.shell = null;
        this.activeStudy = null;
        const publishableKey = assertNonEmpty(options.publishableKey, 'publishableKey');
        this.publishableKey = publishableKey;
        this.apiClient = new SdkApiClient(publishableKey);
        this.logger = options.logger ?? noopLogger;
        this.store = new BrowserStore(`pillow-sdk:${shortHash(publishableKey)}`);
        this.runtime = new BrowserAudienceRuntime({
            publishableKey,
            baseUrl: normalizeBaseUrl(options.baseUrl),
            logger: this.logger,
            store: this.store,
        });
    }
    setExternalId(externalId) {
        this.catchAndLog(this.runtime.setExternalId(externalId), 'Failed to set PillowSDK external ID');
    }
    setUserProperty(key, value) {
        this.catchAndLog(this.runtime.setUserProperty(key, value), 'Failed to set PillowSDK user property', { key });
    }
    clearUserProperty(key) {
        this.catchAndLog(this.runtime.clearUserProperty(key), 'Failed to clear PillowSDK user property', { key });
    }
    clearAllProperties() {
        this.catchAndLog(this.runtime.clearAllProperties(), 'Failed to clear PillowSDK user properties');
    }
    async reset() {
        this.writeStudySessions({});
        await this.runtime.reset();
    }
    async presentStudy(study, options = {}) {
        const studyId = assertNonEmpty(study.id, 'study.id');
        if (this.activeStudy?.completed) {
            this.shell?.destroy();
            this.shell = null;
            this.activeStudy = null;
        }
        if (this.activeStudy && !this.activeStudy.completed) {
            if (this.shell && !this.shell.isVisible()) {
                this.shell.destroy();
                this.shell = null;
                this.activeStudy = null;
            }
            else {
                const error = new Error('A study is already being presented');
                this.emit('studyError', { study, error });
                throw error;
            }
        }
        try {
            const sessionToken = await this.runtime.onForeground();
            const prepareResponse = await this.apiClient.prepareCampaign(this.runtime.currentApiBaseUrl(), sessionToken, studyId, options.skipIfAlreadyExposed === true);
            if (!prepareResponse.should_present || !prepareResponse.study_url || !prepareResponse.campaign_handoff_token) {
                this.emit('studySkip', { study });
                return;
            }
            const presentationOptions = mergePresentationOptions(undefined, options.presentation);
            const presentationMode = resolvePresentationMode(presentationOptions);
            const restoredSessionToken = options.forceFreshSession === true ? null : this.readStudySessions()[studyId] ?? null;
            const iframeUrl = buildStudyIframeUrl({
                studyUrl: prepareResponse.study_url,
                campaignHandoffToken: prepareResponse.campaign_handoff_token,
                restoredSessionToken,
                forceFreshSession: options.forceFreshSession === true,
                presentationMode,
            });
            if (this.shell) {
                this.shell.destroy();
            }
            this.shell = new WidgetShell(presentationOptions, {
                onBridgeMessage: (message) => this.handleBridgeMessage(studyId, message),
                onClose: () => {
                    this.logger.info('PillowSDK widget closed', { studyId });
                },
                onOpen: () => {
                    this.logger.info('PillowSDK widget opened', { studyId });
                },
            });
            this.shell.mount(iframeUrl);
            this.shell.open();
            this.activeStudy = {
                study: { id: studyId },
                prepareResponse,
                presentationMode,
                completed: false,
                didFinish: false,
            };
            this.emit('studyPresent', { study: { id: studyId } });
        }
        catch (error) {
            const normalized = normalizeError(error, 'Failed to present study');
            this.emit('studyError', { study, error: normalized });
            throw normalized;
        }
    }
    on(event, listener) {
        const listeners = this.listeners[event];
        listeners.add(listener);
        return () => {
            this.off(event, listener);
        };
    }
    off(event, listener) {
        const listeners = this.listeners[event];
        listeners.delete(listener);
    }
    close() {
        this.shell?.close();
    }
    toggle() {
        this.shell?.toggle();
    }
    clearSession() {
        const activeStudy = this.activeStudy;
        if (!activeStudy || !this.shell) {
            return;
        }
        if (!activeStudy.prepareResponse.study_url || !activeStudy.prepareResponse.campaign_handoff_token) {
            return;
        }
        const sessions = this.readStudySessions();
        delete sessions[activeStudy.study.id];
        this.writeStudySessions(sessions);
        this.shell.requestClearSession();
        this.shell.reload(buildStudyIframeUrl({
            studyUrl: activeStudy.prepareResponse.study_url,
            campaignHandoffToken: activeStudy.prepareResponse.campaign_handoff_token,
            restoredSessionToken: null,
            forceFreshSession: true,
            presentationMode: activeStudy.presentationMode,
        }));
        this.shell.open();
    }
    destroy() {
        this.shell?.destroy();
        this.shell = null;
        this.runtime.dispose();
        this.activeStudy = null;
    }
    handleBridgeMessage(studyId, message) {
        const activeStudy = this.activeStudy;
        if (!activeStudy || activeStudy.study.id !== studyId) {
            return;
        }
        switch (message.type) {
            case 'pillow:studySession': {
                const alias = typeof message.data?.alias === 'string' ? message.data.alias.trim() : '';
                const sessionToken = typeof message.data?.sessionToken === 'string' ? message.data.sessionToken.trim() : '';
                if (alias && sessionToken) {
                    const next = this.readStudySessions();
                    next[alias] = sessionToken;
                    this.writeStudySessions(next);
                }
                break;
            }
            case 'pillow:conversationEnded': {
                const alias = typeof message.data?.alias === 'string' ? message.data.alias.trim() : '';
                if (alias) {
                    const next = this.readStudySessions();
                    delete next[alias];
                    this.writeStudySessions(next);
                }
                activeStudy.completed = true;
                if (!activeStudy.didFinish) {
                    activeStudy.didFinish = true;
                    this.emit('studyFinish', { study: activeStudy.study });
                }
                break;
            }
            default:
                break;
        }
    }
    readStudySessions() {
        return this.store.readJson(this.sessionStoreKey) ?? {};
    }
    writeStudySessions(next) {
        this.store.writeJson(this.sessionStoreKey, next);
    }
    catchAndLog(operation, message, context) {
        void operation.catch((error) => {
            this.logger.error(message, {
                ...context,
                error: normalizeError(error, message).message,
            });
        });
    }
    emit(event, payload) {
        const listeners = this.listeners[event];
        for (const listener of listeners) {
            try {
                listener(payload);
            }
            catch (error) {
                this.logger.error('PillowSDK event listener failed', {
                    event,
                    error: normalizeError(error, 'Event listener failed').message,
                });
            }
        }
    }
}
//# sourceMappingURL=sdk.js.map